/**
 * One site's System Alerts page — the local node's own, and the cloud's for the site picked in the
 * title bar (read through its tunnel). Same page, same records (the site's system_alerts), and the
 * same layout as the Alert List, so the Alerts pages read as one set:
 *
 *   ┌ SYSTEM ALERT FILTERS ┐ ┌ [System alerts 2] [System errors 1] ─────────────────────────┐
 *   │ search               │ │ ALERT (name · severity, machine · message) WHEN  LAST SEEN    │
 *   │ STATUS  Not fixed 3  │ │ TIMES  STATUS  HANDLED BY  ACTIONS (✓ acknowledge, ✓○ resolve) │
 *   │ WHAT    CPU / Disk…  │ │ …                                                            │
 *   │ MACHINE node-main …  │ └──────────────────────────────────────────────────────────────┘
 *   └──────────────────────┘
 *
 *   System alerts   CPU / memory / disk too high on one of its machines, a camera not streaming
 *   System errors   a service that stopped or keeps failing
 *
 * One row = one incident, from its start until it recovered; TIMES is how many times it was
 * still there after another cooldown (or happened again). A row opens its details. Acknowledge
 * stops it being passed on to more people; Resolve asks for the post-incident report.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CheckCircleIcon, CheckIcon, CircleStackIcon, CpuChipIcon, ServerStackIcon, Square3Stack3DIcon, VideoCameraSlashIcon,
} from '@heroicons/react/24/outline';
import PageTabs from '../common/PageTabs';
import Modal from '../common/Modal';
import { Callout } from '../common/Callout';
import { FilterColumn, FilterSection, FilterCheck, FilterPick, FilterSearch, PageWithFilters, useFilterColumn } from '../layout/FilterColumn';
import { AlertStatusTag, SeverityTag, alertSeverityVar } from '../alerts/AlertTags';
import ResolveReport, { type ResolveReportValue } from './ResolveReport';
import {
  alertActionsErrorText,
  causeLabel,
  durationText,
  type EscalationsApi,
  type SiteSystemAlert,
} from './alertActionsModel';
import { BTN_PRIMARY, BTN_PRIMARY_SM, BTN_SECONDARY, BTN_SECONDARY_SM } from '../common/ui';

export interface SystemAlertsViewProps {
  /** The site's own API, or the cloud's proxy for one site (both already exist for Escalations). */
  api: Pick<EscalationsApi, 'systemAlerts' | 'setSystemAlertStatus'>;
  /** May acknowledge / resolve (alerts.edit). */
  canEdit: boolean;
  /** Shown instead of the list when the site cannot be read (the cloud: the site is offline). */
  unavailable?: ReactNode;
  /** Bump to reload now (the page title's Refresh). */
  reloadToken?: number;
  /** How many are not resolved — for the page title's subtitle. */
  onCounts?: (c: { alerts: number; errors: number }) => void;
}

type Tab = 'alerts' | 'errors';
type StatusFilter = 'live' | 'open' | 'acknowledged' | 'resolved' | 'all';

const KIND_ICON: Record<string, typeof CpuChipIcon> = { service: ServerStackIcon, cpu: CpuChipIcon, memory: Square3Stack3DIcon, disk: CircleStackIcon, camera: VideoCameraSlashIcon };
const KIND_WORD: Record<string, string> = { service: 'Service', cpu: 'CPU', memory: 'Memory', disk: 'Disk', camera: 'Camera' };
const ALERT_KINDS = ['cpu', 'memory', 'disk', 'camera'];
const STATUSES: { value: StatusFilter; label: string }[] = [
  { value: 'live', label: 'Not resolved' },
  { value: 'open', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];
const isLive = (r: SiteSystemAlert) => r.status !== 'resolved';
const isError = (r: SiteSystemAlert) => r.kind === 'service';
/** The Alert List's vocabulary: an open incident is "new". */
const statusWord = (r: SiteSystemAlert) => (r.status === 'open' ? 'new' : r.status);
const whereOf = (r: SiteSystemAlert) => (isError(r) ? r.machine || '' : r.machine || r.service || '');
const whatOf = (r: SiteSystemAlert) => (isError(r) ? r.service || 'Service' : KIND_WORD[r.kind] || r.kind);

const ago = (iso: string | null) => (iso ? `${durationText(Math.max(60, Math.round((Date.now() - new Date(iso).getTime()) / 60000) * 60))} ago` : '—');
const timeOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase() : '—');
const dateOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: '2-digit' }) : '');
const full = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '');
const lastedOf = (r: SiteSystemAlert) => (r.resolved_at
  ? durationText(Math.max(60, Math.round((new Date(r.resolved_at).getTime() - new Date(r.first_at).getTime()) / 60000) * 60))
  : '');

/** Who has it: on it / resolved by / recovered by itself. */
function handledOf(r: SiteSystemAlert): { who: string; how: string } | null {
  if (r.status === 'resolved') return r.resolved_how === 'auto' ? { who: 'Recovered by itself', how: '' } : { who: r.resolved_by || 'Resolved', how: 'resolved it' };
  if (r.acknowledged_by) return { who: r.acknowledged_by, how: 'is on it' };
  return null;
}

const TH = 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';

function ActionBtn({ title, onClick, disabled, className = '', children }: { title: string; onClick: () => void; disabled?: boolean; className?: string; children: ReactNode }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} disabled={disabled}
      className={`flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 ${className}`}>
      {children}
    </button>
  );
}

export default function SystemAlertsView({ api, canEdit, unavailable, reloadToken = 0, onCounts }: SystemAlertsViewProps) {
  const [tab, setTab] = useState<Tab>('alerts');
  const [rows, setRows] = useState<SiteSystemAlert[] | null>(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<StatusFilter>('live');
  const [what, setWhat] = useState('');
  const [machine, setMachine] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [fixing, setFixing] = useState<SiteSystemAlert | null>(null);
  const [report, setReport] = useState<ResolveReportValue>({ cause: '', note: '' });
  const filterCol = useFilterColumn('system-alerts');

  const load = useCallback(async () => {
    if (unavailable) return;
    try {
      setRows(await api.systemAlerts('all'));
      setError('');
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not read the system alerts'));
      setRows((r) => r || []);
    }
  }, [api, unavailable]);
  useEffect(() => { void load(); }, [load, reloadToken]);
  // Fresh every 30 s while the page is open — something may have recovered by itself.
  useEffect(() => {
    if (unavailable) return undefined;
    const t = setInterval(() => { if (!document.hidden) void load(); }, 30000);
    return () => clearInterval(t);
  }, [load, unavailable]);
  // A filter that belongs to the other tab does not follow you there.
  useEffect(() => { setWhat(''); setMachine(''); }, [tab]);

  const all = rows || [];
  const inTab = useMemo(() => all.filter((r) => (tab === 'errors' ? isError(r) : !isError(r))), [all, tab]);
  const byStatus = (s: StatusFilter, list = inTab) => list.filter((r) => (s === 'all' ? true : s === 'live' ? isLive(r) : r.status === s));
  const needle = q.trim().toLowerCase();
  const matches = (r: SiteSystemAlert) => !needle || [r.title, r.message, r.machine, r.service, r.acknowledged_by, r.resolved_by].some((x) => String(x || '').toLowerCase().includes(needle));
  // Counts in the column follow the OTHER filters, so each number is what clicking it shows.
  const base = inTab.filter(matches);
  const whatKey = (r: SiteSystemAlert) => (tab === 'errors' ? r.service || '' : r.kind);
  const withStatus = byStatus(status, base);
  const shown = withStatus.filter((r) => (!what || whatKey(r) === what) && (!machine || whereOf(r) === machine));
  const whatOptions = tab === 'errors'
    ? [...new Set(inTab.map((r) => r.service || '').filter(Boolean))].sort()
    : ALERT_KINDS.filter((k) => inTab.some((r) => r.kind === k));
  const machineOptions = [...new Set(inTab.map(whereOf).filter(Boolean))].sort();
  const activeFilters = [needle, status !== 'live', what, machine].filter(Boolean).length;
  const clearFilters = () => { setQ(''); setStatus('live'); setWhat(''); setMachine(''); };
  const opened = openId ? all.find((r) => r.id === openId) || null : null;
  const liveAlerts = all.filter((r) => !isError(r) && isLive(r)).length;
  const liveErrors = all.filter((r) => isError(r) && isLive(r)).length;
  useEffect(() => { if (rows) onCounts?.({ alerts: liveAlerts, errors: liveErrors }); }, [rows, liveAlerts, liveErrors]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (row: SiteSystemAlert, action: 'acknowledge' | 'resolve') => {
    setBusy(row.id);
    try {
      const next = await api.setSystemAlertStatus(row.id, action, action === 'resolve'
        ? { ...(report.cause ? { cause: report.cause } : {}), ...(report.note.trim() ? { note: report.note.trim() } : {}) }
        : undefined);
      setRows((list) => (list || []).map((r) => (r.id === row.id ? { ...r, ...next } : r)));
      if (action === 'resolve') { setFixing(null); setReport({ cause: '', note: '' }); }
      setError('');
    } catch (err) {
      setError(alertActionsErrorText(err, `Could not ${action === 'resolve' ? 'resolve it' : 'acknowledge it'}`));
    } finally {
      setBusy(null);
    }
  };
  const startResolve = (r: SiteSystemAlert) => { setFixing(r); setReport({ cause: '', note: '' }); };

  if (unavailable) return <Callout tone="info">{unavailable}</Callout>;

  const errorsTab = tab === 'errors';

  const actions = (r: SiteSystemAlert, big = false) => (canEdit && isLive(r) ? (
    big ? (
      <div className="flex flex-wrap justify-end gap-2">
        {r.status === 'open' && (
          <button type="button" disabled={busy === r.id} onClick={() => void act(r, 'acknowledge')}
            className={BTN_SECONDARY_SM}>
            <CheckIcon className="h-4 w-4" /> Acknowledge
          </button>
        )}
        <button type="button" disabled={busy === r.id} onClick={() => startResolve(r)}
          className={BTN_PRIMARY_SM}>
          <CheckCircleIcon className="h-4 w-4" /> Resolve
        </button>
      </div>
    ) : (
      <div className="flex items-center justify-end gap-0.5">
        {r.status === 'open' && (
          <ActionBtn title="Acknowledge — someone is on it" disabled={busy === r.id} onClick={() => void act(r, 'acknowledge')}>
            <CheckIcon className="h-4 w-4" />
          </ActionBtn>
        )}
        <ActionBtn title="Resolve" disabled={busy === r.id} className="hover:text-emerald-500" onClick={() => startResolve(r)}>
          <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
        </ActionBtn>
      </div>
    )
  ) : null);

  return (
    <PageWithFilters
      filters={(
        <FilterColumn width="wide" open={filterCol.open} onToggle={filterCol.toggle} label="Filters" activeCount={activeFilters} onClearAll={clearFilters}>
          <FilterSearch value={q} onChange={setQ} placeholder={errorsTab ? 'Service, message or person' : 'Machine, camera or message'} />
          <FilterSection title="Status">
            {STATUSES.map((s) => (
              <FilterCheck key={s.value} radio checked={status === s.value} onClick={() => setStatus(s.value)} label={s.label} count={byStatus(s.value, base).length} />
            ))}
          </FilterSection>
          {whatOptions.length > 0 && (
            <FilterSection title={errorsTab ? 'Service' : 'What'}>
              <FilterPick
                label={errorsTab ? 'Service' : 'What'}
                allLabel={errorsTab ? 'All services' : 'Everything'}
                allCount={withStatus.filter((r) => !machine || whereOf(r) === machine).length}
                value={what}
                onChange={setWhat}
                options={whatOptions.map((w) => ({ value: w, label: errorsTab ? w : KIND_WORD[w], count: withStatus.filter((r) => whatKey(r) === w && (!machine || whereOf(r) === machine)).length }))}
                searchPlaceholder={errorsTab ? 'Search services…' : undefined}
              />
            </FilterSection>
          )}
          {machineOptions.length > 1 && (
            <FilterSection title={errorsTab ? 'Machine' : 'Where'}>
              <FilterPick
                label={errorsTab ? 'Machine' : 'Where'}
                allLabel="Everywhere"
                allCount={withStatus.filter((r) => !what || whatKey(r) === what).length}
                value={machine}
                onChange={setMachine}
                options={machineOptions.map((m) => ({ value: m, label: m, count: withStatus.filter((r) => whereOf(r) === m && (!what || whatKey(r) === what)).length }))}
                searchPlaceholder={errorsTab ? 'Search machines…' : 'Search machines and cameras…'}
              />
            </FilterSection>
          )}
        </FilterColumn>
      )}
      // As on the Alert List: the tabs are a white strip across the top of the results.
      toolbar={(
        <PageTabs
          active={tab}
          onChange={(k) => setTab(k as Tab)}
          className="px-2 !border-b-0"
          tabs={[
            { key: 'alerts', label: 'System alerts', count: rows ? liveAlerts : null },
            { key: 'errors', label: 'System errors', count: rows ? liveErrors : null },
          ]}
        />
      )}
    >
      {/* The table keeps a white surface of its own; the tabs are the toolbar above. */}
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
        {error && <div className="border-b border-border px-4 py-2"><Callout tone="danger">{error}</Callout></div>}

        <div className="min-w-0 overflow-x-auto">
          {rows === null ? (
            <div className="space-y-2 p-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />)}</div>
          ) : shown.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
              <CheckCircleIcon className="h-9 w-9 text-emerald-500 opacity-70" />
              <p className="text-sm font-medium text-foreground">
                {activeFilters ? 'Nothing matches these filters' : errorsTab ? 'No service errors — every service is running' : 'All clear — no system alerts'}
              </p>
              <p className="text-[13px] text-muted-foreground">
                {activeFilters ? 'Try another status, or clear the filters.' : errorsTab ? 'A service that stops or keeps failing shows here.' : 'CPU, memory or disk too high, or a camera not streaming, shows here.'}
              </p>
              {activeFilters > 0 && <button type="button" onClick={clearFilters} className="mt-1 text-sm text-brand">Clear filters</button>}
            </div>
          ) : (
            <table className="w-full min-w-[56rem] table-fixed text-[13px]">
              <colgroup>
                <col className="w-[38%]" />
                <col className="w-[11%]" />
                <col className="w-[10%]" />
                <col className="w-[7%]" />
                <col className="w-[13%]" />
                <col className="w-[13%]" />
                <col className="w-[8%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className={`${TH} pl-4`}>{errorsTab ? 'Error' : 'Alert'}</th>
                  <th className={TH}>Started</th>
                  <th className={TH}>Last seen</th>
                  <th className={TH} title="1 + every time it was still there after another cooldown, or happened again">Times</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Handled by</th>
                  <th className={`${TH} pr-4 text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const Icon = KIND_ICON[r.kind] || ServerStackIcon;
                  const live = isLive(r);
                  const where = whereOf(r);
                  const h = handledOf(r);
                  return (
                    <tr key={r.id} tabIndex={0} onClick={() => setOpenId(r.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenId(r.id); } }}
                      className={`group cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted focus:bg-muted/40 focus:outline-none ${live ? '' : 'opacity-60'}`}>
                      <td className="py-2.5 pl-4 pr-3" style={{ boxShadow: `inset 3px 0 0 ${alertSeverityVar(r.severity)}` }}>
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"><Icon className="h-[18px] w-[18px]" /></span>
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="truncate font-semibold text-foreground" title={r.title || undefined}>{r.title || whatOf(r)}</span>
                              <SeverityTag severity={r.severity} variant="soft" dot={false} className="shrink-0" />
                              {(r.escalation_level || 0) > 1 && live && (
                                <span className="shrink-0 rounded-md bg-amber-500/10 px-1.5 py-px text-[11px] font-medium text-amber-700">Passed on · step {r.escalation_level}</span>
                              )}
                            </div>
                            <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] text-muted-foreground">
                              <span className="max-w-[14rem] shrink-0 truncate font-medium text-foreground/80" title={where}>{where || whatOf(r)}</span>
                              {r.message && (<><span className="shrink-0 opacity-50">·</span><span className="truncate" title={r.message}>{r.message}</span></>)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5" title={full(r.first_at)}>
                        <div className="font-medium text-foreground">{timeOf(r.first_at)}</div>
                        <div className="text-[12px] text-muted-foreground">{dateOf(r.first_at)}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-foreground" title={full(r.last_at)}>{ago(r.last_at)}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-foreground">{r.occurrences > 1 ? `×${r.occurrences}` : '1'}</td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <AlertStatusTag status={statusWord(r)} />
                        {r.suppressed && live && <div className="mt-1 text-[11px] text-muted-foreground">{r.suppressed === 'parent' ? 'Held back — its service is down' : 'Paused — nobody told'}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        {h ? (
                          <span className="block truncate text-foreground" title={h.who}>{h.who}</span>
                        ) : <span className="text-muted-foreground">Nobody yet</span>}
                        {r.status === 'resolved' && <span className="block truncate text-[12px] text-muted-foreground">lasted {lastedOf(r)}</span>}
                      </td>
                      <td className="px-3 py-2.5 pr-4" onClick={(e) => e.stopPropagation()}>
                        {actions(r) || <span className="block text-right text-[11px] text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {rows && rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-[12px] text-muted-foreground">
            <span>{shown.length} of {inTab.length} {errorsTab ? 'errors' : 'alerts'} · refreshed every 30 s</span>
            <span>Acknowledge stops it being passed on to more people. Most of these resolve by themselves when the problem goes away.</span>
          </div>
        )}
      </div>

      {/* ── One incident, in full ── */}
      <Modal open={!!opened} onClose={() => setOpenId(null)} size="lg" eyebrow={opened ? (isError(opened) ? 'System error' : 'System alert') : undefined}
        title={opened ? opened.title || whatOf(opened) : ''}
        footer={opened ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1"><AlertStatusTag status={statusWord(opened)} /></span>
            {actions(opened, true)}
            <button type="button" onClick={() => setOpenId(null)} className={BTN_SECONDARY}>Close</button>
          </div>
        ) : undefined}>
        {opened && (
          <div className="space-y-4 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <SeverityTag severity={opened.severity} />
              {(opened.escalation_level || 0) > 1 && <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-[12px] font-medium text-amber-700">Passed on to step {opened.escalation_level}</span>}
              {opened.suppressed && isLive(opened) && <span className="rounded-md bg-muted px-2 py-0.5 text-[12px] text-muted-foreground">{opened.suppressed === 'parent' ? 'Held back — its service is down' : 'Paused — nobody was told'}</span>}
            </div>
            {opened.message && <p className="rounded-lg border border-border bg-muted px-3 py-2.5 text-[13px] text-foreground">{opened.message}</p>}
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-[13px] sm:grid-cols-2">
              {[
                [isError(opened) ? 'Service' : 'What', whatOf(opened)],
                [isError(opened) ? 'Machine' : 'Where', whereOf(opened) || '—'],
                ['Started', full(opened.first_at)],
                ['Last seen', `${full(opened.last_at)} (${ago(opened.last_at)})`],
                ['Times', opened.occurrences > 1 ? `${opened.occurrences} — still there after each cooldown, or happened again` : 'Once'],
                opened.status === 'resolved'
                  ? ['Ended', `${full(opened.resolved_at)} · lasted ${lastedOf(opened)}`]
                  : ['Going on for', ago(opened.first_at).replace(' ago', '')],
                opened.acknowledged_by ? ['Acknowledged', `${opened.acknowledged_by}${opened.acknowledged_at ? ` · ${full(opened.acknowledged_at)}` : ''}`] : null,
                opened.status === 'resolved' ? ['Resolved', opened.resolved_how === 'auto' ? `Recovered by itself${opened.resolved_by ? ` — ${opened.resolved_by}` : ''}` : opened.resolved_by || '—'] : null,
                opened.cause ? ['Cause', causeLabel(opened.cause)] : null,
                opened.resolution_note ? ['What was done', opened.resolution_note] : null,
              ].filter(Boolean).map((row) => {
                const [k, v] = row as [string, string];
                return (
                  <div key={k} className="min-w-0">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{k}</dt>
                    <dd className="mt-0.5 break-words text-foreground">{v}</dd>
                  </div>
                );
              })}
            </dl>
          </div>
        )}
      </Modal>

      <Modal
        open={!!fixing}
        onClose={() => setFixing(null)}
        size="md"
        title="Resolve"
        subtitle={fixing ? fixing.title || whatOf(fixing) : undefined}
        footer={(
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFixing(null)} className={BTN_SECONDARY}>Cancel</button>
            <button type="button" disabled={!fixing || busy === fixing.id} onClick={() => fixing && void act(fixing, 'resolve')}
              className={BTN_PRIMARY}>
              <CheckCircleIcon className="h-4 w-4" /> Resolve
            </button>
          </div>
        )}
      >
        <div className="p-5"><ResolveReport value={report} onChange={setReport} /></div>
      </Modal>
    </PageWithFilters>
  );
}
